/* cart-shipping-line.js – CRO-60 (2026-10-16): moved unchanged from snippets/cart-shipping-line.liquid (register_only) (was an inline <script> on every page). */
  if (!window.customElements.get('cart-shipping-line')) {
    (() => {
      // Sample addresses: Shopify only returns rates for a complete address. Delivery zones are
      // country-wide, so any valid postal code of the country returns that country's rate.
      const SAMPLE = {
        LT: ['01100'], LV: ['LV-1050'], EE: ['10111'], PL: ['00-001'], FI: ['00100'], SE: ['111 20'],
        DK: ['1050'], NO: ['0150'], DE: ['10115'], AT: ['1010'], CH: ['8001'], NL: ['1012 JS'],
        BE: ['1000'], LU: ['1009'], FR: ['75001'], ES: ['28001', 'Madrid'], PT: ['1100-148', 'Lisboa'],
        IT: ['00118', 'Roma'], IE: ['D02 X285', 'Dublin'], GB: ['SW1A 1AA'], IS: ['101'], CZ: ['110 00'],
        SK: ['811 01'], HU: ['1051'], SI: ['1000'], HR: ['10000'], RO: ['010011', 'B'], BG: ['1000'],
        GR: ['105 57'], CY: ['1010'], MT: ['VLT 1117'], US: ['10001', 'New York'], CA: ['M5H 2N2', 'Ontario'],
        AU: ['2000', 'NSW'], NZ: ['6011', 'Wellington'], JP: ['100-0001', 'Tokyo'], AE: ['', 'Dubai'],
        IL: ['6100000'], SG: ['018956'], MX: ['06600', 'CMX'], BR: ['01310-100', 'SP'], AR: ['C1002', 'C'],
        CL: ['8320000', 'RM'], CO: ['110111', 'DC'], PE: ['15001', 'LIM'], IN: ['110001', 'DL'], CN: ['100000', 'BJ'],
        HK: ['', 'HK'], TW: ['100'], KR: ['04524', 'KR-11'], MY: ['50000', 'KUL'], TH: ['10200', 'TH-10'],
        ID: ['10110', 'JK'], PH: ['1000', '00'], VN: ['100000'], ZA: ['0001', 'GP'], EG: ['11511', 'C'], MA: ['20000'],
        TR: ['34000'], UA: ['01001'], RS: ['11000'], BA: ['71000'], MK: ['1000'], AL: ['1001'], ME: ['81000'],
        MD: ['2001'], GE: ['0105'], AM: ['0010'], SA: ['11564'], QA: [''], KW: ['', 'KW-KU'], BH: [''], OM: ['100'],
        LI: ['9490'], AD: ['AD500'], MC: ['98000'], SM: ['47890'], VA: ['00120'], GI: ['GX11 1AA'], FO: ['100'],
        JE: ['JE2 3AA'], GG: ['GY1 1AA'], IM: ['IM1 1AA'], AX: ['22100'], XK: ['10000'], DO: ['10101'],
        CR: ['10101', 'CR-SJ'], PA: ['', 'PA-8'], EC: ['170150'], GT: ['01001', 'GUA'], NG: ['100001', 'LA'], KE: ['00100']
      };
      const cache = new Map();

      // CRO10-D3: order cutoff 15:00 Lithuanian time (Europe/Vilnius). An order placed after it on a business
      // day starts processing one business day later, the same way checkout dates move.
      const CUTOFF_HOUR = 15;
      const afterCutoff = () => {
        try {
          const parts = new Intl.DateTimeFormat('en-GB', { timeZone: 'Europe/Vilnius', weekday: 'short', hour: 'numeric', hourCycle: 'h23' }).formatToParts(new Date());
          const get = (type) => (parts.find((part) => part.type === type) || {}).value;
          const weekday = get('weekday');
          return weekday !== 'Sat' && weekday !== 'Sun' && parseInt(get('hour'), 10) >= CUTOFF_HOUR;
        } catch (error) {
          return false;
        }
      };

      const formatMoney = (cents, format) => {
        const value = (cents / 100).toFixed(2);
        const [int, dec] = value.split('.');
        const group = (sep) => int.replace(/\B(?=(\d{3})+(?!\d))/g, sep);
        return format.replace(/<[^>]*>/g, '').replace(/\{\{\s*(\w+)\s*\}\}/, (match, key) => {
          switch (key) {
            case 'amount_no_decimals': return group(',');
            case 'amount_with_comma_separator': return group('.') + ',' + dec;
            case 'amount_no_decimals_with_comma_separator': return group('.');
            case 'amount_with_apostrophe_separator': return group("'") + '.' + dec;
            case 'amount_with_space_separator': return group(' ') + ',' + dec;
            default: return group(',') + '.' + dec;
          }
        });
      };

      class CartShippingLine extends HTMLElement {
        connectedCallback() {
          // When the parser creates the element its children are not there yet.
          if (!this.querySelector('[data-total]')) {
            setTimeout(() => this.connectedCallback(), 0);
            return;
          }
          const country = this.dataset.country;
          if (!country) return;
          const key = [country, this.dataset.currency, this.dataset.totalCents, this.dataset.itemCount].join('|');
          if (cache.has(key)) {
            this.render(cache.get(key));
            return;
          }
          this.fetchRate(country).then((rate) => {
            cache.set(key, rate);
            if (this.isConnected) this.render(rate);
          }).catch(() => {});
        }

        async fetchRate(country) {
          const sample = SAMPLE[country] || [];
          const params = new URLSearchParams();
          params.set('shipping_address[country]', country);
          params.set('shipping_address[zip]', sample[0] || '');
          params.set('shipping_address[province]', sample[1] || '');
          const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
          const response = await fetch(`${root}cart/shipping_rates.json?${params.toString()}`, { credentials: 'same-origin' });
          if (!response.ok) return null;
          const data = await response.json();
          const rates = (data && data.shipping_rates) || [];
          if (!rates.length) return null;
          // DEV-599: in Lithuania NoParcels adds parcel-locker rates (DPD, LP Express, Omniva) next to the home courier.
          // The cheapest of all still drives the estimated total; the lockers and the courier are named separately.
          const cheapest = (list) => (list.length ? list.reduce((best, rate) => (parseFloat(rate.price) < parseFloat(best.price) ? rate : best)) : null);
          const isLocker = (rate) => /paštomat|parcel ?locker|pickup/i.test(rate.name || '');
          const best = cheapest(rates);
          best.lmLocker = cheapest(rates.filter(isLocker));
          best.lmCourier = cheapest(rates.filter((rate) => !isLocker(rate)));
          return best;
        }

        render(rate) {
          if (!rate) return;
          if (rate.currency && rate.currency !== this.dataset.currency) return;
          const settings = (window.themeVariables && window.themeVariables.settings) || {};
          const format = settings.moneyWithCurrencyFormat || settings.moneyFormat;
          if (!format) return;

          const feeCents = Math.round(parseFloat(rate.price) * 100);
          // Some delivery methods have no transit time set in Shopify; then the sentence goes without days.
          // CRO10-D3: delivery_days is transit only, while checkout dates add the store's order processing
          // time (Settings > Shipping and delivery > Order processing time = 2 business days), so it is added here.
          // DEV-590: items with their own production time (custom.production_time) raise the processing days.
          const PROCESSING_DAYS = (parseInt(this.dataset.processing, 10) || 2) + (afterCutoff() ? 1 : 0);
          // AUD-1013-F05 (G, 2026-10-13): transit days come from the same zone table as the product page (LT 1–2, EU/US 3–5,
          // elsewhere 5–8). Shopify's delivery_days depend on the delivery profile of the products in the cart (ANA is in its
          // own "Free shipping" profile), so one country showed 3–4 or 7–10 days depending on the cart. Shopify's days are
          // used only when the zone is not known.
          const zoneDays = (this.dataset.zoneDays || '').split(/[–-]/).map((n) => parseInt(n, 10)).filter((n) => n > 0);
          const [transitFrom, transitTo] = zoneDays.length ? zoneDays : (Array.isArray(rate.delivery_days) ? rate.delivery_days : []);
          const from = transitFrom ? transitFrom + PROCESSING_DAYS : transitFrom;
          const to = transitTo ? transitTo + PROCESSING_DAYS : transitTo;
          // DEV-590: when every item is made to order, the days are in the made-to-order line, not here.
          const days = from && this.dataset.mtoAll !== 'true' ? (to && to !== from ? `${from}–${to}` : `${from}`) : '';
          const transit = transitFrom ? (transitTo && transitTo !== transitFrom ? `${transitFrom}–${transitTo}` : `${transitFrom}`) : '';
          this.querySelectorAll('[data-transit]').forEach((node) => { if (transit) node.textContent = transit; });
          const fee = formatMoney(feeCents, settings.moneyFormat || format);
          const template = feeCents === 0
            ? (days ? this.dataset.freeText : this.dataset.freeNoDaysText)
            : (days ? this.dataset.paidText : this.dataset.paidNoDaysText);
          let sentence = template.replace('%%FEE%%', () => fee).replace('%%DAYS%%', () => days);
          // DEV-599: parcel lockers and the home courier in one line, with the courier's days (lockers have none in Shopify).
          const locker = rate.lmLocker;
          const courier = rate.lmCourier;
          if (locker && courier && this.dataset.lockerPaidText) {
            const lockerCents = Math.round(parseFloat(locker.price) * 100);
            const courierCents = Math.round(parseFloat(courier.price) * 100);
            const [cFrom, cTo] = zoneDays.length ? zoneDays : (Array.isArray(courier.delivery_days) ? courier.delivery_days : []);
            const cA = cFrom ? cFrom + PROCESSING_DAYS : 0;
            const cB = cTo ? cTo + PROCESSING_DAYS : 0;
            const cDays = cA && this.dataset.mtoAll !== 'true' ? (cB && cB !== cA ? `${cA}–${cB}` : `${cA}`) : '';
            const lockerTemplate = lockerCents === 0 && courierCents === 0 ? this.dataset.lockerFreeText : this.dataset.lockerPaidText;
            sentence = lockerTemplate
              .replace('%%LOCKER%%', () => formatMoney(lockerCents, settings.moneyFormat || format))
              .replace('%%COURIER%%', () => formatMoney(courierCents, settings.moneyFormat || format))
              .replace(/\[([^\]]*)\]/, (match, part) => (cDays ? part.replace('%%DAYS%%', cDays) : ''))
              .replace(/\.\./g, '.');
          }

          this.querySelector('[data-rate]').textContent = sentence;
          this.querySelector('[data-total-label]').textContent = this.dataset.estimateLabel;
          this.querySelector('[data-total]').textContent = formatMoney(parseInt(this.dataset.totalCents, 10) + feeCents, format);
          this.querySelectorAll('[data-estimate]').forEach((node) => { node.hidden = false; });
          this.querySelectorAll('[data-fallback]').forEach((node) => { node.hidden = true; });
          // DEV-645: on /cart the phone bar at the bottom shows this same estimated total, not the amount without delivery.
          const goSum = this.closest('form') && this.closest('form').querySelector('[data-lm645-sum]');
          if (goSum) {
            goSum.querySelector('[data-lm645-label]').textContent = goSum.dataset.estimateLabel;
            goSum.querySelector('[data-lm645-total]').textContent = formatMoney(parseInt(this.dataset.totalCents, 10) + feeCents, settings.moneyFormat || format);
          }
        }
      }

      window.customElements.define('cart-shipping-line', CartShippingLine);
    })();
  }
